import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import Header from "./Header";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom map icon
const mapIcon = new L.Icon({
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Fetch address from coordinates using Nominatim (OpenStreetMap)
const fetchAddressFromCoordinates = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'AdminApp/1.0'
        }
      }
    );
    const data = await response.json();
    if (data && data.display_name) {
      return data.display_name;
    }
    return null;
  } catch (error) {
    console.error("Error fetching address:", error);
    return null;
  }
};

function MapModal({ isOpen, onClose, customer }) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [address, setAddress] = useState("");
  const [loadingAddress, setLoadingAddress] = useState(false);

  useEffect(() => {
    if (isOpen && customer && customer.Latitude && customer.Longitude && mapContainerRef.current) {
      const lat = parseFloat(customer.Latitude);
      const lng = parseFloat(customer.Longitude);

      // Clear existing map if any
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }

      // Clear container
      mapContainerRef.current.innerHTML = "";

      // Create new map
      const newMap = L.map(mapContainerRef.current).setView([lat, lng], 15);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(newMap);

      const marker = L.marker([lat, lng], { icon: mapIcon })
        .addTo(newMap)
        .bindPopup(`<b>${customer.Vname}</b><br>Loading address...`)
        .openPopup();

      mapRef.current = newMap;

      // Fetch address from coordinates
      setLoadingAddress(true);
      fetchAddressFromCoordinates(lat, lng).then((fetchedAddress) => {
        setAddress(fetchedAddress || "");
        setLoadingAddress(false);
        
        // Update marker popup with actual address
        if (fetchedAddress) {
          marker.setPopupContent(`<b>${customer.Vname}</b><br>${fetchedAddress}`);
        } else {
          marker.setPopupContent(`<b>${customer.Vname}</b><br>${customer.Address1 || ""}`);
        }
      });
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      setAddress("");
    };
  }, [isOpen, customer]);

  if (!isOpen || !customer) return null;

  const hasLocation = customer.Latitude && customer.Longitude && 
                     parseFloat(customer.Latitude) !== 0 && 
                     parseFloat(customer.Longitude) !== 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-2xl mx-4 bg-white rounded-lg shadow-xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-800">
            Location - {customer.Vname}
          </h3>
          <button
            onClick={onClose}
            className="text-2xl font-bold text-gray-500 hover:text-gray-700"
          >
            &times;
          </button>
        </div>
        <div className="p-4">
          {!hasLocation ? (
            <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg">
              <div className="text-center">
                <p className="mb-2 text-lg text-gray-600">📍</p>
                <p className="text-gray-500">No location data available</p>
                <p className="text-sm text-gray-400">
                  Latitude: {customer.Latitude || "N/A"}, Longitude: {customer.Longitude || "N/A"}
                </p>
              </div>
            </div>
          ) : (
            <div 
              ref={mapContainerRef}
              className="z-0 w-full rounded-lg h-80"
              style={{ minHeight: "320px" }}
            />
          )}
          {hasLocation && (
            <div className="mt-3 space-y-2 text-sm text-gray-600">
              <p><b>Original Address:</b> {customer.Address1 || "-"}</p>
              {loadingAddress ? (
                <p><b>Coordinates Address:</b> <span className="text-gray-400">Loading...</span></p>
              ) : address ? (
                <p><b>Coordinates Address:</b> {address}</p>
              ) : null}
              <p><b>Coordinates:</b> {customer.Latitude}, {customer.Longitude}</p>
            </div>
          )}
        </div>
        <div className="flex justify-end p-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-white bg-gray-500 rounded hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CustomerExplr() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [mapCustomer, setMapCustomer] = useState(null);

  const handleLogout = () => {
    sessionStorage.removeItem("isLoggedIn");
    sessionStorage.removeItem("username");
    sessionStorage.removeItem("lastVisited");
    window.location.href = "/";
  };

  const openMap = (customer) => {
    setMapCustomer(customer);
  };

  const closeMap = () => {
    setMapCustomer(null);
  };

  const exportToCSV = () => {
    if (!filteredCustomers.length) {
      alert("No data to export");
      return;
    }

    const headers = [
      "Name",
      "Phone",
      "Email",
      "Address",
      "City",
      "Pincode",
    ];

    const rows = filteredCustomers.map((c) => [
      c.Vname || "",
      c.VCode || "",
      c.Mail || "",
      `${c.Address1 || ""} ${c.Address2 || ""}`.trim(),
      c.City || "",
      c.Pincode || "",
    ]);

    const csvContent =
      [headers, ...rows]
        .map((row) =>
          row
            .map((value) =>
              `"${String(value).replace(/"/g, '""')}"`
            )
            .join(",")
        )
        .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Customer_Master_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await api.CustomerListExplrAPI("admin");
      const data = response?.CustomerListExplrAPIjs?.Table || [];
      data.sort((a, b) => b.Vid - a.Vid);
      setCustomers(data);
      setFilteredCustomers(data);
    } catch (error) {
      console.error("Error fetching customers:", error);
      alert("Failed to fetch customers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!searchTerm) {
        setFilteredCustomers(customers);
      } else {
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = customers.filter(
          (c) =>
            c.Vname?.toLowerCase().includes(lowerSearch) ||
            c.VCode?.includes(searchTerm) ||
            c.City?.toLowerCase().includes(lowerSearch) ||
            c.Customermode?.toLowerCase().includes(lowerSearch)
        );
        setFilteredCustomers(filtered);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, customers]);

  const handleBack = () => {
    navigate("/dashboard");
  };

  const handleNew = () => {
    navigate("/customers/new");
  };

  const handleEdit = (customer) => {
    navigate(`/customers/edit/${customer.Vid || customer.id}`, { state: { customer } });
  };

  const hasLocation = (customer) => {
    return customer.Latitude && customer.Longitude && 
           parseFloat(customer.Latitude) !== 0 && 
           parseFloat(customer.Longitude) !== 0;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onLogout={handleLogout} />
      <main className="p-6 md:ml-64">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleBack}
            className="inline-flex items-center justify-center px-3 py-1 font-semibold text-gray-800 transition bg-gray-200 rounded-full shadow-md hover:bg-gray-300"
          >
            ← Back
          </button>
          <h2 className="flex-1 text-2xl font-bold text-center text-orange-600">
            Customer Master
          </h2>
          
        </div>

        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={handleNew}
            className="inline-flex items-center justify-center px-3 py-1 font-semibold text-white bg-orange-500 rounded-full shadow-md hover:bg-orange-600"
          >
            + New
          </button>
          <input
            type="text"
            placeholder="Search by Name, Mobile, Area, or Customer Type"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="
              flex-1
              px-2 py-1.5
              text-sm
              border border-gray-300
              rounded-md
              focus:outline-none
              focus:ring-1 focus:ring-orange-400
            "
          />

          <button
            onClick={exportToCSV}
            className="
              px-2.5 py-1.5
              text-sm font-semibold
              inline-flex items-center
              bg-blue-500 text-white
              rounded-full
              hover:bg-blue-700
              shadow
              transition
              whitespace-nowrap
            "
          >
            📄 Export
          </button>
        </div>


        {loading ? (
          <div className="font-semibold text-black">Loading customers...</div>
        ) : (
          <div className="p-4 overflow-x-auto">
            <table className="w-full overflow-hidden bg-white border-gray-300 shadow-lg rounded-2xl" style={{ tableLayout: 'fixed' }}>
              <thead className="text-white bg-orange-500">
                <tr>
                  <th className="w-40 px-2 py-2 text-left border-b">Name</th>
                  <th className="w-32 px-2 py-2 text-left border-b">Phone</th>
                  <th className="w-40 px-2 py-2 text-left border-b">Mail</th>
                  <th className="w-64 px-2 py-2 text-left border-b">Address</th>
                  <th className="w-32 px-2 py-2 text-left border-b">City</th>
                  <th className="w-24 px-2 py-2 text-left border-b">Pincode</th>
                  <th className="w-20 px-2 py-2 text-left border-b">Map</th>
                  <th className="w-20 px-2 py-2 text-left border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer, index) => (
                  <tr key={customer.Vid || index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="px-2 py-2 font-medium text-black truncate border-b">{customer.Vname || "-"}</td>
                    <td className="px-2 py-2 text-black truncate border-b">{customer.Phone || "-"}</td>
                    <td className="px-2 py-2 text-black truncate border-b">{customer.Mail || "-"}</td>
                    <td className="px-2 py-2 text-black truncate border-b" title={`${customer.Address1 || ""} ${customer.Address2 || ""}`.trim() || "-"}>{`${customer.Address1 || ""} ${customer.Address2 || ""}`.trim() || "-"}</td>
                    <td className="px-2 py-2 text-black truncate border-b">{customer.City || "-"}</td>
                    <td className="px-2 py-2 text-black truncate border-b">{customer.Pincode || "-"}</td>
                    <td className="px-2 py-2 text-center border-b">
                      <button
                        onClick={() => openMap(customer)}
                        className={`px-2 py-1 text-sm rounded ${
                          hasLocation(customer)
                            ? "text-white bg-green-500 hover:bg-green-600"
                            : "text-gray-400 bg-gray-200 cursor-not-allowed"
                        }`}
                        disabled={!hasLocation(customer)}
                        title={hasLocation(customer) ? "View on Map" : "No location data"}
                      >
                        📍 Map
                      </button>
                    </td>
                    <td className="px-2 py-2 text-center border-b">
                      <button
                        onClick={() => handleEdit(customer)}
                        className="px-2 py-1 text-sm text-white bg-blue-500 rounded hover:bg-blue-600"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredCustomers.length === 0 && (
                  <tr>
                    <td colSpan="8" className="p-4 text-center text-black">
                      No customers found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
      
      <MapModal isOpen={!!mapCustomer} onClose={closeMap} customer={mapCustomer} />
    </div>
  );
}
